/*
 * Copyright (c) 2021 NOOGA DEVELOPMENT, All rights reserved.
 * Author Julia Romanovskaya <julia.romanovskaya@noogadev.com>
 */

import { createAction } from '../../utils/redux-utils';
import {
    DetailedUserDto,
    DetailedUsersListDto,
    DetailedUsersListFilterParams,
    NewUserData,
} from '../api/users/usersClient.typings';
import { PageQueryParams } from '../api/apiCommon.typings';
import { Notification, NOTIFICATION_TYPES } from '../../utils/utils';
import { i18n } from '../translation/i18n';

export enum AdminUsersActionType {
    OPEN_ADMIN_USERS_LIST_PAGE = 'AdminUsersList/ open page',
    EXIT_ADMIN_USERS_LIST_PAGE = 'AdminUsersList/ exit page',

    APPLY_PAGING = 'AdminUsersList/ apply paging',
    APPLY_FILTER = 'AdminUsersList/ apply filter',

    CHANGE_FILTERS = 'AdminUsersList/ change filters',

    FETCH_USERS_LIST_SUCCESS = 'AdminUsersList/ fetch users list success',
    FETCH_USERS_LIST_ERROR = 'AdminUsersList/ fetch users list error',

    ADD_USER = 'AdminUsersList/ add user',
    DELETE_USER = 'AdminUsersList/ delete user',
    EDIT_USER = 'AdminUsersList/ edit user',
    BLOCK_USER = 'AdminUsersList/ block user',
    UNBLOCK_USER = 'AdminUsersList/ unblock user',

    ADD_USER_SUCCESS = 'AdminUsersList/ add user success',
    ADD_USER_ERROR = 'AdminUsersList/ add user error',
    ADD_USER_DIALOG_TOGGLE = 'AdminUsersList/ add user dialog toggle',
    EDIT_USER_DIALOG_TOGGLE = 'AdminUsersList/ edit user dialog toggle',

    UPDATE_USER_SUCCESS = 'AdminUsersList/ update user info success',
    UPDATE_USER_ERROR = 'AdminUsersList/ update user info error',

    DOWNLOAD_EXCEL = 'AdminUsersList/ download users list as excel',
    DOWNLOAD_EXCEL_SUCCESS = 'AdminUsersList/ download users list as excel success',
    DOWNLOAD_EXCEL_ERROR = 'AdminUsersList/ download users list as excel error',
}

export const openAdminUsersListAction = () => createAction(AdminUsersActionType.OPEN_ADMIN_USERS_LIST_PAGE);

export const exitAdminUsersListAction = () => createAction(AdminUsersActionType.EXIT_ADMIN_USERS_LIST_PAGE);

export const applyPagingAction = (params: PageQueryParams) => createAction(AdminUsersActionType.APPLY_PAGING, params);

export const applyFilteringAction = () => createAction(AdminUsersActionType.APPLY_FILTER);

export const changeFiltersAction = (params: DetailedUsersListFilterParams) =>
    createAction(AdminUsersActionType.CHANGE_FILTERS, params);

export const fetchUsersListSuccessAction = (list: DetailedUsersListDto) =>
    createAction(AdminUsersActionType.FETCH_USERS_LIST_SUCCESS, {
        list,
        notification:
            list.count === 0
                ? {
                      type: NOTIFICATION_TYPES[1],
                      message: i18n.t('errors.notFoundByParams'),
                  }
                : null,
    });

export const fetchUsersListErrorAction = (notification: Notification) =>
    createAction(AdminUsersActionType.FETCH_USERS_LIST_ERROR, { notification });

export const addUserAction = (userInfo: Partial<NewUserData>) => createAction(AdminUsersActionType.ADD_USER, userInfo);

export const addUserDialogToggleAction = (isOpen: boolean) =>
    createAction(AdminUsersActionType.ADD_USER_DIALOG_TOGGLE, isOpen);

export const editUserDialogToggleAction = (isOpen: boolean) =>
    createAction(AdminUsersActionType.EDIT_USER_DIALOG_TOGGLE, isOpen);

export const deleteUserAction = (userId: number) => createAction(AdminUsersActionType.DELETE_USER, userId);

export const editUserAction = (userId: number, userInfo: Partial<NewUserData>) =>
    createAction(AdminUsersActionType.EDIT_USER, { userId, userInfo });

export const blockUserAction = (userId: number) => createAction(AdminUsersActionType.BLOCK_USER, userId);

export const unblockUserAction = (userId: number) => createAction(AdminUsersActionType.UNBLOCK_USER, userId);

export const updateUserSuccessAction = (payload: DetailedUserDto) =>
    createAction(AdminUsersActionType.UPDATE_USER_SUCCESS, payload);

export const updateUserErrorAction = (notification: Notification) =>
    createAction(AdminUsersActionType.UPDATE_USER_ERROR, { notification });

export const addUserSuccessAction = (payload: DetailedUserDto) =>
    createAction(AdminUsersActionType.ADD_USER_SUCCESS, {
        user: payload,
        notification: {
            type: NOTIFICATION_TYPES[1],
            message: i18n.t('SystemScreens.userAdded'),
        },
    });

export const addUserErrorAction = (notification: Notification) =>
    createAction(AdminUsersActionType.ADD_USER_ERROR, { notification });

export const downloadUsersAsExcelAction = () => createAction(AdminUsersActionType.DOWNLOAD_EXCEL);

export const downloadUsersAsExcelSuccessAction = () => createAction(AdminUsersActionType.DOWNLOAD_EXCEL_SUCCESS);

export const downloadUsersAsExcelErrorAction = (notification: Notification) =>
    createAction(AdminUsersActionType.DOWNLOAD_EXCEL_ERROR, { notification });

export type AdminUsersAction =
    | ReturnType<typeof openAdminUsersListAction>
    | ReturnType<typeof exitAdminUsersListAction>
    | ReturnType<typeof applyPagingAction>
    | ReturnType<typeof applyFilteringAction>
    | ReturnType<typeof changeFiltersAction>
    | ReturnType<typeof fetchUsersListSuccessAction>
    | ReturnType<typeof fetchUsersListErrorAction>
    | ReturnType<typeof addUserAction>
    | ReturnType<typeof addUserDialogToggleAction>
    | ReturnType<typeof editUserDialogToggleAction>
    | ReturnType<typeof deleteUserAction>
    | ReturnType<typeof editUserAction>
    | ReturnType<typeof blockUserAction>
    | ReturnType<typeof unblockUserAction>
    | ReturnType<typeof updateUserSuccessAction>
    | ReturnType<typeof updateUserErrorAction>
    | ReturnType<typeof addUserSuccessAction>
    | ReturnType<typeof addUserErrorAction>
    | ReturnType<typeof downloadUsersAsExcelAction>
    | ReturnType<typeof downloadUsersAsExcelSuccessAction>
    | ReturnType<typeof downloadUsersAsExcelErrorAction>;
